
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { clearSessionData } from '@/utils/sessionSecurity';
import { logSecurityEvent } from '@/utils/security';

export const useSignOut = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const signOut = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    console.log("Sign out process started");
    
    try {
      // Log security event
      logSecurityEvent('user_logout_initiated');
      
      // Use centralized session data clearing for security
      clearSessionData();
      console.log("All session data cleared securely");
      
      // Sign out from Supabase with global scope to ensure all tabs are signed out
      console.log("Calling supabase.auth.signOut() with global scope");
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'global' });
      
      if (signOutError) {
        console.error("Error from Supabase signOut:", signOutError);
        logSecurityEvent('logout_error', { error: signOutError.message });
        throw signOutError;
      }
      
      logSecurityEvent('user_logout_success');
      console.log("Sign out completed successfully");
      toast({
        title: "Signed out successfully",
        description: "You have been logged out of your account.",
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unknown error occurred during sign out');
      console.error('Error signing out:', error.message);
      
      setError(error);
      
      toast({
        title: "Sign out issue",
        description: "There was an issue signing out. Please try again.",
        variant: "destructive",
      });
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { signOut, isLoading, error };
};
